import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, IconButton, Menu, Tooltip, Typography, Divider } from '@mui/material';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import { Event, User } from '../../services/Types';
import { SendANotification } from '../SendANotification/SendANotification';
import { DialogDeleteEvent } from '../Dialogs/Delete';
import { DialogCloseout } from '../Dialogs/Closeout';
import { StyledMenuItem } from './ActionMenus.styles';
import { useEvent } from '../../hooks/useEvent';
import { usePermissions } from '../../hooks/usePermissions';
import { DialogRevertEvent } from '../Dialogs/Revert';
import { DialogCancelEvent } from '../Dialogs/Cancel';
import { DialogApproveEvent } from '../Dialogs/Approve';
import { openEventComments, openVersionHistory } from '../../utils/sidebarUrl';
import { canUserAccessEventComments } from '../../utils/eventCommentsAccess';

interface ActionMenusProps {
  eventData: Event;
}

export const ActionMenus: React.FC<ActionMenusProps> = ({ eventData }) => {
  const navigate = useNavigate()
  const [showRevertDialog, setShowRevertDialog] = useState(false)
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [showApproveDialog, setShowApproveDialog] = useState(false)

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  // event closeout
  const [openEventCloseout, setOpenEventCloseout] = useState(false)
  const handleOpenEventCloseout = () => {
    setOpenEventCloseout(true)
  }
  const handleCloseEventCloseout = () => {
    setOpenEventCloseout(false)
    handleClose()
  }

  // delete events
  const [deleteEventId, setDeleteEventId] = useState(0)
  const onDelete = () => {
    // TODO: refresh events list?
    setDeleteEventId(0)
    setAnchorEl(null)
  }
  const onDeleteCancel = () => {
    setDeleteEventId(0)
    setAnchorEl(null)
  }
  const deleteEvent = () => {
    setDeleteEventId(eventData.id)
    setAnchorEl(null)
  }

  const [openDailog, setOpenDailog] = useState(false)

  const [requestType, setRequestType] = useState('Select')
  const {
    handleSendNotification,
    logMessage,
    setLogMessage
  } = useEvent();

  let user: User = JSON.parse(localStorage.getItem('user') || '{}')
  const canAccessComments = canUserAccessEventComments(user)

  const { showButtonReject, showButtonApprove, showButtonRevert, showButtonCancel, showButtonDelete, eventIsBlocked, userIsApprover, userIsAdmin } = usePermissions(eventData);

  const editEvent = () => {
    window.open(`/event/review/${eventData.id}`, '_blank', 'noopener noreferrer')
    setAnchorEl(null)
  }

  const sendANotification = async () => {
    const success = await handleSendNotification(eventData.id, requestType);
    if (success) {
      setOpenDailog(false)
      setAnchorEl(null)
    }
  }

  return eventData ? (
    <div>
      <IconButton
        aria-controls={open ? 'demo-positioned-menu' : undefined}
        aria-haspopup='true'
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        size="large">
        <MoreVertOutlinedIcon sx={{ fontSize: 24 }} color='secondary' />
      </IconButton>
      {/* Menu bar */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        PaperProps={{
          style: {
            width: 160
          }
        }}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <StyledMenuItem onClick={editEvent}>
          View/Edit
        </StyledMenuItem>
        {!eventIsBlocked && (
          <StyledMenuItem onClick={() => {
            openVersionHistory(eventData.id)
            handleClose()
          }}>
            Version History
          </StyledMenuItem>
        )}
        {!eventIsBlocked && canAccessComments && (
          <StyledMenuItem onClick={() => {
            openEventComments(eventData.id)
            handleClose()
          }}>
            Comments
          </StyledMenuItem>
        )}
        {userIsApprover && <Divider />}
        {userIsApprover && (
          <StyledMenuItem
            disabled={!showButtonApprove}
            onClick={() => setShowApproveDialog(true)}
          >
            Approve
          </StyledMenuItem>
        )}
        {userIsApprover && (
          <StyledMenuItem
            disabled={!showButtonReject}
            onClick={() => setOpenDailog(true)}
          >
            Revision Request
          </StyledMenuItem>
        )}
        {userIsAdmin && <Divider />}
        {userIsAdmin && (
          <StyledMenuItem
            disabled={!showButtonRevert}
            onClick={() => setShowRevertDialog(true)}
          >
            Revert
          </StyledMenuItem>
        )}
        {userIsAdmin && (
          <StyledMenuItem
            disabled={!showButtonCancel}
            onClick={() => {
              setShowCancelDialog(true)
              handleClose()
          }}>
            Cancel
          </StyledMenuItem>
        )}
        {userIsAdmin && (
          <StyledMenuItem
            disabled={!showButtonDelete}
            onClick={deleteEvent}
          >
            Delete
          </StyledMenuItem>
        )}
      </Menu>
      {/* Send Notification */}
      <SendANotification
        open={openDailog}
        onClose={() => setOpenDailog(false)}
        onPrimaryButtonClick={sendANotification}
        onSecondaryButtonClick={() => setOpenDailog(false)}
        requestType={requestType}
        setRequestType={setRequestType}
        logMessage={logMessage}
        setLogMessage={setLogMessage}
      />
      {deleteEventId > 0 && (
        <DialogDeleteEvent
          eventId={deleteEventId}
          onSuccess={onDelete}
          onCancel={onDeleteCancel}
        />
      )}
      {openEventCloseout && (
        <DialogCloseout
          event={eventData}
          onClose={handleCloseEventCloseout}
        />
      )}
      {showApproveDialog && (
        <DialogApproveEvent
          eventId={eventData.id}
          onSuccess={() => {
            setShowApproveDialog(false)
          }}
          onCancel={() => setShowApproveDialog(false)}
        />
      )}
      {showRevertDialog && (
        <DialogRevertEvent
          eventId={eventData.id}
          onSuccess={() => {
            setShowRevertDialog(false)
          }}
          onCancel={() => setShowRevertDialog(false)}
        />
      )}
      {showCancelDialog && (
        <DialogCancelEvent
          eventId={eventData.id}
          onSuccess={() => {
            setShowCancelDialog(false);
          }}
          onCancel={() => setShowCancelDialog(false)}
        />
      )}
    </div>
  ) : null;

  
}; 